import React from "react";
import { useTranslation } from "react-i18next";
import Chip from "@/components/ui/chip";
import IconContainer from "@/components/ui/icon-container";
import {
  ClockAlertOverdue,
  Draft,
  Error as ErrorIcon,
  Loader,
  Resolve,
  Send,
  Terminate,
  XFailed
} from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { Proposal, ProposalStatus } from "./types";

interface BadgeStyle {
  icon: React.ReactNode;
  fillClassName: string;
  textClassName: string;
  labelKey: string;
}

// Delivered has no badge of its own in the design, so it shares Sent's.
const STATUS_BADGE_STYLES: Record<ProposalStatus, BadgeStyle> = {
  [ProposalStatus.DRAFT]: {
    icon: <Draft />,
    fillClassName: "bg-badges-default",
    textClassName: "text-text-body-primary",
    labelKey: "contracts.proposals.status.draft"
  },
  [ProposalStatus.SENT]: {
    icon: <Send />,
    fillClassName: "bg-badges-new",
    textClassName: "text-text-new",
    labelKey: "contracts.proposals.status.sent"
  },
  [ProposalStatus.DELIVERED]: {
    icon: <Send />,
    fillClassName: "bg-badges-new",
    textClassName: "text-text-new",
    labelKey: "contracts.proposals.status.delivered"
  },
  [ProposalStatus.SIGNED]: {
    icon: <Resolve />,
    fillClassName: "bg-badges-positive",
    textClassName: "text-text-positive",
    labelKey: "contracts.proposals.status.signed"
  },
  [ProposalStatus.DECLINED]: {
    icon: <XFailed />,
    fillClassName: "bg-badges-negative",
    textClassName: "text-text-negative",
    labelKey: "contracts.proposals.status.declined"
  },
  [ProposalStatus.EXPIRED]: {
    icon: <ClockAlertOverdue />,
    fillClassName: "bg-badges-default",
    textClassName: "text-text-body-primary",
    labelKey: "contracts.proposals.status.expired"
  },
  [ProposalStatus.VOIDED]: {
    icon: <Terminate />,
    fillClassName: "bg-badges-warning",
    textClassName: "text-text-warning",
    labelKey: "contracts.proposals.status.voided"
  },
  [ProposalStatus.BOUNCED]: {
    icon: <ErrorIcon />,
    fillClassName: "bg-badges-negative",
    textClassName: "text-text-negative",
    labelKey: "contracts.proposals.status.bounced"
  }
};

const PARTIALLY_SIGNED_STYLE: BadgeStyle = {
  icon: <Loader />,
  fillClassName: "bg-badges-natural",
  textClassName: "text-text-natural",
  labelKey: "contracts.proposals.status.partiallySigned"
};

// A status this app doesn't know yet, so a new one never breaks the row.
const UNKNOWN_STATUS_STYLE: BadgeStyle = {
  icon: <Draft />,
  fillClassName: "bg-badges-default",
  textClassName: "text-text-body-primary",
  labelKey: "contracts.proposals.status.unknown"
};

// Own-property only, so a status like "toString" falls back too.
const statusStyle = (status: string): BadgeStyle =>
  Object.prototype.hasOwnProperty.call(STATUS_BADGE_STYLES, status)
    ? STATUS_BADGE_STYLES[status as ProposalStatus]
    : UNKNOWN_STATUS_STYLE;

type BadgeProposal = Pick<Proposal, "status" | "signedCount" | "signerCount">;

const isPartiallySigned = ({
  status,
  signedCount,
  signerCount
}: BadgeProposal) =>
  (status === ProposalStatus.SENT || status === ProposalStatus.DELIVERED) &&
  !!signedCount &&
  !!signerCount &&
  signedCount < signerCount;

interface Props {
  proposal: BadgeProposal;
  // In place of the status's own label, as for a recipient who opened it.
  label?: string;
}

export const ProposalStatusBadge: React.FC<Props> = ({ proposal, label }) => {
  const { t } = useTranslation();
  const style = isPartiallySigned(proposal)
    ? PARTIALLY_SIGNED_STYLE
    : statusStyle(proposal.status);

  return (
    <Chip
      className={cn("h-7", style.fillClassName, style.textClassName)}
      textVariant="body"
      textClassName={style.textClassName}
      icon={
        <IconContainer size="sm" as="span">
          {style.icon}
        </IconContainer>
      }
      label={
        label ??
        t(style.labelKey, {
          signed: proposal.signedCount,
          total: proposal.signerCount
        })
      }
      data-test="proposal-status-badge"
    />
  );
};
